<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:preview>
            <!-- Bộ lọc (§7.7) -->
            <div class="col-md-12 mb-5 pl-0">
              <b-row>
                <b-col md="3" class="pl-0">
                  <date-picker
                    placeholder="Từ ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.fromDate"
                  ></date-picker>
                </b-col>
                <b-col md="3">
                  <date-picker
                    placeholder="Đến ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.toDate"
                  ></date-picker>
                </b-col>
                <b-col md="4">
                  <!-- Ngành cửa hàng: tìm cửa hàng -->
                  <vue-autosuggest
                    v-if="!isCrReport"
                    v-model="filter.storeText"
                    :suggestions="filteredStores"
                    @selected="onSelectedStore"
                    :limit="10"
                    @input="onInputStore"
                    :input-props="{
                      id: 'autosuggest__input',
                      placeholder: 'Tìm cửa hàng',
                    }"
                    :should-render-suggestions="
                      (size, loading) => size >= 0 && !loading
                    "
                  >
                    <template slot-scope="{ suggestion }">
                      <div>{{ suggestion.item.suggestionName }}</div>
                    </template>
                  </vue-autosuggest>
                  <!-- CR: chọn bộ phận -->
                  <b-form-select
                    v-else
                    size="sm"
                    v-model="filter.departmentId"
                    :options="departmentOptions"
                    value-field="id"
                    text-field="name"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null"
                        >-- Chọn bộ phận --</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-col>
                <b-col md="2">
                  <b-button
                    style="width: 70px"
                    variant="primary"
                    size="sm"
                    :disabled="onLoading"
                    @click="onFilter"
                    >Lọc</b-button
                  >
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col md="12" class="pl-0">
                  <b-form-checkbox
                    v-model="filter.onlyWithCommission"
                    size="sm"
                    @change="onFilter"
                  >
                    Ẩn dòng 0đ (vẫn hiện dòng âm)
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </div>

            <!-- Chương trình đang áp dụng trong kỳ -->
            <div v-if="programs.length" class="col-md-12 mb-4 pl-0">
              <span class="text-muted mr-2"
                >Chương trình áp dụng trong kỳ:</span
              >
              <span
                v-for="program in programs"
                :key="program.id"
                class="label label-inline label-light-primary font-weight-bold mr-2 mb-1"
                :title="programPeriod(program)"
                >{{ program.name }}</span
              >
            </div>

            <!-- Khối tổng quan (§7.4) -->
            <div class="col-md-12 mb-5 pl-0">
              <b-row>
                <b-col
                  v-for="card in summaryCards"
                  :key="card.key"
                  md="3"
                  class="mb-3"
                >
                  <div class="summary-card" :class="{ 'is-total': card.strong }">
                    <div class="summary-card__label">{{ card.label }}</div>
                    <div
                      class="summary-card__value"
                      :class="card.isMoney ? amountClass(card.raw) : ''"
                    >
                      {{ card.value }}
                    </div>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Bảng tổng hợp — không phân trang (§7.12) -->
            <b-table
              :items="displayItems"
              :fields="fields"
              bordered
              hover
              :busy="onLoading"
              show-empty
              :empty-text="emptyText"
              class="report-table"
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="10"></vcl-table>
              </template>
              <template v-slot:cell(count)="row">
                <span>{{ row.index + 1 }}</span>
              </template>
              <template v-slot:cell(quantity)="row">
                <span>{{ displayQuantity(row.item.quantity) }}</span>
              </template>
              <template v-slot:cell(commissionAmount)="row">
                <span :class="amountClass(row.item.commissionAmount)">{{
                  displayAmount(row.item.commissionAmount)
                }}</span>
              </template>
              <template v-slot:cell(adhocAmount)="row">
                <span :class="amountClass(row.item.adhocAmount)">{{
                  displayAmount(row.item.adhocAmount)
                }}</span>
              </template>
              <template v-slot:cell(bonusAmount)="row">
                <span :class="amountClass(row.item.bonusAmount)">{{
                  displayAmount(row.item.bonusAmount)
                }}</span>
              </template>
              <template v-slot:cell(minusAmount)="row">
                <span>{{ displayAmount(row.item.minusAmount) }}</span>
              </template>
              <template v-slot:cell(totalAmount)="row">
                <span
                  class="font-weight-bold"
                  :class="amountClass(row.item.totalAmount)"
                  >{{ displayAmount(row.item.totalAmount) }}</span
                >
              </template>
              <template v-slot:cell(actions)="row">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="report-detail-btn"
                  title="Xem chi tiết hoa hồng của nhân viên"
                  @click="goToDetail(row.item)"
                >
                  <i class="fas fa-eye"></i>
                  Chi tiết
                </b-button>
              </template>

              <!-- Dòng tổng cộng -->
              <template v-slot:custom-foot>
                <tr class="report-table__foot" v-if="displayItems.length">
                  <td colspan="4" class="text-right font-weight-bold">
                    Tổng cộng
                  </td>
                  <td class="text-center font-weight-bold">
                    {{ displayQuantity(footTotals.quantity) }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="amountClass(footTotals.commissionAmount)"
                  >
                    {{ displayAmount(footTotals.commissionAmount) }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="amountClass(footTotals.adhocAmount)"
                  >
                    {{ displayAmount(footTotals.adhocAmount) }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="amountClass(footTotals.bonusAmount)"
                  >
                    {{ displayAmount(footTotals.bonusAmount) }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ displayAmount(footTotals.minusAmount) }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="amountClass(footTotals.totalAmount)"
                  >
                    {{ displayAmount(footTotals.totalAmount) }}
                  </td>
                  <!-- ô trống khớp với cột thao tác, giữ dòng tổng không lệch cột -->
                  <td></td>
                </tr>
              </template>
            </b-table>

            <b-row v-if="!onLoading && displayItems.length">
              <b-col>
                <p class="mt-3 text-dark" style="font-weight: 500">
                  Tổng số nhân sự: {{ displayItems.length }}
                </p>
              </b-col>
            </b-row>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import datePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import { VclTable } from 'vue-content-loading';
import { makeToastFaile } from '@/utils/common';
import { COMMISSION_REPORT_TYPE_NAME } from './commission.constants';
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

const EMPTY_TOTALS = Object.freeze({
  employeeCount: 0,
  quantity: 0,
  commissionAmount: 0,
  adhocAmount: 0,
  bonusAmount: 0,
  minusAmount: 0,
  totalAmount: 0,
});

export default {
  components: {
    KTCodePreview,
    VclTable,
    datePicker,
  },
  mixins: [commissionReportMixin],
  data() {
    return {
      onLoading: false,
      hasSearched: false,

      items: [],
      programs: [],
      totals: { ...EMPTY_TOTALS },
      period: null,
      store: null,

      storeOptions: [],
      departmentOptions: [],
      filteredStores: [{ data: [] }],

      filter: {
        fromDate: moment().startOf('month').format(DATE_INPUT_FORMAT),
        toDate: moment().format(DATE_INPUT_FORMAT),
        storeId: null,
        storeText: '',
        departmentId: null,
        // Mặc định ẩn dòng Tổng thưởng = 0 cho gọn.
        // Dòng ÂM vẫn hiện (vd bị trừ từ cộng/trừ khác) — đó là tiền thật cần thấy.
        onlyWithCommission: true,
      },

      fields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: thStyle('5%'),
          tdClass: 'text-center',
        },
        {
          key: 'employeeCode',
          label: 'Mã NV',
          thStyle: thStyle('9%'),
        },
        {
          key: 'employeeName',
          label: 'Tên nhân viên',
          thStyle: thStyle('18%'),
        },
        {
          key: 'jobTitleName',
          label: 'Chức vụ',
          thStyle: thStyle('13%'),
        },
        {
          key: 'quantity',
          label: 'Số lượng',
          thStyle: thStyle('8%'),
          tdClass: 'text-center',
        },
        {
          key: 'commissionAmount',
          label: 'Hoa hồng',
          thStyle: thStyle('11%'),
          tdClass: 'text-right',
        },
        {
          key: 'adhocAmount',
          label: 'AdHoc',
          thStyle: thStyle('9%'),
          tdClass: 'text-right',
        },
        {
          key: 'bonusAmount',
          label: 'Cộng khác',
          thStyle: thStyle('9%'),
          tdClass: 'text-right',
        },
        {
          key: 'minusAmount',
          label: 'Trừ khác',
          thStyle: thStyle('9%'),
          tdClass: 'text-right',
        },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
        {
          key: 'actions',
          label: '',
          thStyle: thStyle('5%'),
          tdClass: 'text-center',
        },
      ],
    };
  },
  computed: {
    pageTitle() {
      return (
        COMMISSION_REPORT_TYPE_NAME[this.reportType] || 'Báo cáo hoa hồng'
      );
    },
    /**
     * §7.7: lọc "chỉ hiện người có hoa hồng" CHỈ ảnh hưởng hiển thị.
     * BE cũng nhận tham số này, nhưng vẫn lọc lại phía FE để đổi checkbox
     * không phải gọi lại API.
     */
    displayItems() {
      if (!this.filter.onlyWithCommission) {
        return this.items;
      }
      return this.items.filter((item) => Number(item.totalAmount) !== 0);
    },
    /** Tổng cuối bảng — tính từ đúng các dòng đang hiển thị */
    footTotals() {
      return this.displayItems.reduce(
        (acc, item) => ({
          quantity: acc.quantity + (Number(item.quantity) || 0),
          commissionAmount:
            acc.commissionAmount + (Number(item.commissionAmount) || 0),
          adhocAmount: acc.adhocAmount + (Number(item.adhocAmount) || 0),
          bonusAmount: acc.bonusAmount + (Number(item.bonusAmount) || 0),
          minusAmount: acc.minusAmount + (Number(item.minusAmount) || 0),
          totalAmount: acc.totalAmount + (Number(item.totalAmount) || 0),
        }),
        {
          quantity: 0,
          commissionAmount: 0,
          adhocAmount: 0,
          bonusAmount: 0,
          minusAmount: 0,
          totalAmount: 0,
        },
      );
    },
    /** Thẻ số liệu tổng quan — lấy từ totals do BE trả về */
    summaryCards() {
      const totals = this.totals || EMPTY_TOTALS;
      return [
        {
          key: 'employeeCount',
          label: 'Số nhân sự',
          value: this.displayQuantity(totals.employeeCount),
          raw: totals.employeeCount,
          isMoney: false,
        },
        {
          key: 'quantity',
          label: 'Số lượng',
          value: this.displayQuantity(totals.quantity),
          raw: totals.quantity,
          isMoney: false,
        },
        {
          key: 'commissionAmount',
          label: 'Hoa hồng',
          value: this.displayAmount(totals.commissionAmount),
          raw: totals.commissionAmount,
          isMoney: true,
        },
        {
          key: 'adhocAmount',
          label: 'AdHoc',
          value: this.displayAmount(totals.adhocAmount),
          raw: totals.adhocAmount,
          isMoney: true,
        },
        {
          key: 'bonusAmount',
          label: 'Cộng khác',
          value: this.displayAmount(totals.bonusAmount),
          raw: totals.bonusAmount,
          isMoney: true,
        },
        {
          key: 'minusAmount',
          label: 'Trừ khác',
          value: this.displayAmount(totals.minusAmount),
          raw: totals.minusAmount,
          isMoney: true,
        },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng',
          value: this.displayAmount(totals.totalAmount),
          raw: totals.totalAmount,
          isMoney: true,
          strong: true,
        },
      ];
    },
    emptyText() {
      if (!this.hasSearched) {
        return `Chọn kỳ và ${this.scopeLabel.toLowerCase()} rồi bấm Lọc để xem báo cáo`;
      }
      if (this.filter.onlyWithCommission && this.items.length) {
        return 'Không có nhân sự nào phát sinh hoa hồng trong kỳ';
      }
      return 'Không có dữ liệu hoa hồng trong kỳ đã chọn';
    },
  },
  watch: {
    '$route.query.type': function () {
      this.resolveReportType();
      this.setBreadcrumb();
      this.resetResult();
      this.fetchScopeOptions();
    },
  },
  created() {
    this.resolveReportType();
    this.setBreadcrumb();
    this.restoreFilterFromQuery();
    this.fetchScopeOptions();
  },
  methods: {
    /**
     * Đọc lại filter từ query string để F5 / chia sẻ link không mất điều kiện lọc.
     * Cửa hàng cần chờ danh sách tải xong mới gán được tên -> xử lý ở restoreStoreFromQuery.
     */
    restoreFilterFromQuery() {
      const query = this.$route.query || {};
      if (query.fromDate) {
        this.filter.fromDate = query.fromDate;
      }
      if (query.toDate) {
        this.filter.toDate = query.toDate;
      }
      if (query.onlyWithCommission !== undefined) {
        this.filter.onlyWithCommission = query.onlyWithCommission !== 'false';
      }
      if (this.isCrReport && query.departmentId) {
        this.filter.departmentId = Number(query.departmentId);
        this.$nextTick(() => this.fetchReport());
      } else if (!this.isCrReport && query.storeId) {
        this.filter.storeId = Number(query.storeId);
      }
    },
    /** Gán lại TÊN cửa hàng sau khi danh sách tải xong, rồi tự tải báo cáo */
    restoreStoreFromQuery() {
      if (this.isCrReport || !this.filter.storeId) {
        return;
      }
      const store = this.storeOptions.find(
        (item) => item.id === this.filter.storeId,
      );
      if (store) {
        this.filter.storeText = store.suggestionName;
        this.fetchReport();
      }
    },
    /** Ghi filter hiện tại lên URL — giữ nguyên ?type= của màn */
    syncFilterToQuery() {
      const query = { type: this.$route.query.type };
      if (this.filter.fromDate) {
        query.fromDate = this.filter.fromDate;
      }
      if (this.filter.toDate) {
        query.toDate = this.filter.toDate;
      }
      if (!this.filter.onlyWithCommission) {
        query.onlyWithCommission = 'false';
      }
      if (this.isCrReport) {
        if (this.filter.departmentId) {
          query.departmentId = this.filter.departmentId;
        }
      } else if (this.filter.storeId) {
        query.storeId = this.filter.storeId;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    setBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Hoa hồng V2' },
        { title: this.pageTitle },
      ]);
    },
    programPeriod(program) {
      const to = program.endDate
        ? this.displayDate(program.endDate)
        : 'không giới hạn';
      return `Áp dụng: ${this.displayDate(program.startDate)} - ${to}`;
    },
    resetResult() {
      this.items = [];
      this.programs = [];
      this.totals = { ...EMPTY_TOTALS };
      this.period = null;
      this.store = null;
      this.hasSearched = false;
      this.filter.storeId = null;
      this.filter.storeText = '';
      this.filter.departmentId = null;
    },

    /* ------------------------ Nạp danh sách phạm vi ----------------------- */
    fetchScopeOptions() {
      if (this.isCrReport) {
        this.fetchDepartments();
      } else {
        this.fetchStores();
      }
    },
    fetchStores() {
      ApiService.setHeader();
      ApiService.get('/stores/getStores')
        .then(({ data }) => {
          this.storeOptions = (data.data || []).map((store) => ({
            id: store.id,
            name: store.name,
            shortName: store.shortName || '',
            suggestionName: store.shortName
              ? `${store.shortName} (${store.name})`
              : store.name,
          }));
          // Dựng sẵn danh sách gợi ý để bấm vào ô là thấy ngay, không phải gõ mới hiện
          this.filteredStores = [{ data: this.filterStoreOptions('') }];
          // Khôi phục cửa hàng đã chọn từ URL (F5 không mất filter)
          this.restoreStoreFromQuery();
        })
        .catch(() => {
          this.storeOptions = [];
          makeToastFaile('Không tải được danh sách cửa hàng!');
        });
    },
    fetchDepartments() {
      ApiService.setHeader();
      ApiService.query('employees/departments')
        .then(({ data }) => {
          this.departmentOptions = data.data || [];
        })
        .catch(() => {
          this.departmentOptions = [];
          makeToastFaile('Không tải được danh sách bộ phận!');
        });
    },
    filterStoreOptions(text) {
      const keyword = (text || '').toLowerCase();
      return this.storeOptions
        .filter(
          (store) =>
            String(store.name).toLowerCase().indexOf(keyword) > -1 ||
            String(store.shortName).toLowerCase().indexOf(keyword) > -1,
        )
        .slice(0, 10);
    },
    onInputStore(text) {
      this.filter.storeText = text;
      if (!text) {
        this.filter.storeId = null;
      }
      this.filteredStores = [{ data: this.filterStoreOptions(text) }];
    },
    onSelectedStore(option) {
      this.filter.storeId = option.item.id;
      this.filter.storeText = option.item.suggestionName;
    },

    /* ------------------------------ Tải dữ liệu --------------------------- */
    onFilter() {
      const rangeError = this.validateDateRange(
        this.filter.fromDate,
        this.filter.toDate,
      );
      if (rangeError) {
        makeToastFaile(rangeError);
        return;
      }
      if (this.isCrReport && !this.filter.departmentId) {
        makeToastFaile('Vui lòng chọn bộ phận cần xem báo cáo!');
        return;
      }
      if (!this.isCrReport && !this.filter.storeId) {
        makeToastFaile('Vui lòng chọn cửa hàng cần xem báo cáo!');
        return;
      }
      this.syncFilterToQuery();
      this.fetchReport();
    },
    fetchReport() {
      this.onLoading = true;

      const params = {
        type: this.reportType,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
        onlyWithCommission: this.filter.onlyWithCommission,
      };
      if (this.isCrReport) {
        params.departmentId = this.filter.departmentId;
      } else {
        params.storeId = this.filter.storeId;
      }

      ApiService.setHeader();
      ApiService.query('commission-reports', { params })
        .then(({ data }) => {
          const result = data.data || {};
          this.items = result.items || [];
          this.programs = result.programs || [];
          this.totals = { ...EMPTY_TOTALS, ...(result.totals || {}) };
          this.period = result.period || null;
          this.store = result.store || null;
          this.hasSearched = true;
        })
        .catch((error) => {
          this.items = [];
          this.programs = [];
          this.totals = { ...EMPTY_TOTALS };
          this.hasSearched = true;
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được báo cáo hoa hồng!'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },

    /* ---------------------- Sang màn chi tiết nhân viên ------------------- */
    goToDetail(item) {
      if (!item || !item.employeeId) {
        return;
      }
      const params = {
        type: this.reportType,
        employeeId: item.employeeId,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
      if (this.isCrReport) {
        if (this.filter.departmentId) {
          params.departmentId = this.filter.departmentId;
        }
      } else {
        const storeId = item.storeId || this.filter.storeId;
        if (storeId) {
          params.storeId = storeId;
        }
      }
      this.$router.push({ name: 'commission-report-detail', query: params });
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #ecf0f3;
}

.report-table__foot td {
  background-color: #f3f6f9;
  border-top: 2px solid #dfe3e8;
}

.summary-card {
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  padding: 0.85rem 1rem;
  height: 100%;
  background-color: #ffffff;
}

.summary-card.is-total {
  background-color: #f3f6f9;
  border-color: #d8dde3;
}

.summary-card__label {
  font-size: 0.85rem;
  color: #7e8299;
  margin-bottom: 0.35rem;
}

.summary-card__value {
  font-size: 1.15rem;
  font-weight: 600;
  color: #181c32;
}
</style>

<!--
  Style cho vue-autosuggest phải để KHÔNG scoped: thư viện tự render input và
  dropdown ở ngoài cây DOM có data-attribute của component, nên style scoped
  không với tới ⇒ ô tìm kiếm mất khung Bootstrap, dropdown đè sai vị trí.
  Dùng đúng bộ style của màn báo cáo hệ cũ để hai màn nhìn giống nhau.
-->
<style>
#autosuggest__input {
  display: block;
  width: 100%;
  height: calc(1.35em + 1.1rem + 2px);
  padding: 0.55rem 0.75rem;
  font-size: 0.925rem;
  font-weight: 400;
  line-height: 1.35;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #e5eaee;
  border-radius: 0.28rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

#autosuggest__input:focus {
  outline: none !important;
  border: 1px solid #92c5f9;
}

/* Bọc tương đối để dropdown bám theo ô input, không trôi ra giữa trang */
.autosuggest__results-container {
  position: relative;
  width: 100%;
}

.autosuggest__results {
  position: absolute;
  background-color: #fff;
  z-index: 1000;
  width: 100%;
  border: 1px solid #e0e0e0;
  max-height: 200px;
  overflow-y: auto;
}

.autosuggest__results ul {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  cursor: pointer;
}

.autosuggest__results ul li {
  padding: 8px;
}

.autosuggest__results-item:hover,
.autosuggest__results-item--highlighted {
  background-color: #ddd;
}

.report-detail-btn {
  white-space: nowrap;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}
</style>
